import { z } from "zod";
import type { CrmRecordType } from "@/lib/api/crm/types";
import { portalDate, strictDecimalString } from "./common";
import { parsePhoneNumberToE164Strict } from "./phoneNumber";

/** Body of POST /crm/developer/records/move-master (mirrors @lhc/crm records/move-master-validation.ts). */
export const MoveCrmRecordMasterRequestValidation = z.object({
  record_id: z.string().uuid(),
  target_master_record_id: z.string().uuid("Choose a master record to move to"),
  expected_revision: z.number().int().min(1),
  reason: z.string().trim().min(1, "A reason is required"),
});

export type MoveCrmRecordMasterRequest = z.infer<typeof MoveCrmRecordMasterRequestValidation>;

/** Body of POST /crm/developer/masters/:id/delete. */
export const DeleteCrmMasterRequestValidation = z.object({
  expected_revision: z.number().int().min(1),
  reason: z.string().trim().min(1, "A reason is required"),
});

export type DeleteCrmMasterRequest = z.infer<typeof DeleteCrmMasterRequestValidation>;

/** Trimmed, never blank: the server rejects empty text. */
const crmText = z.string().trim().min(1);
const crmTagIds = z.array(z.string().uuid());
/** The portal requires at least one tag on every contact and address. */
const requiredCrmTagIds = crmTagIds.min(1, "Choose at least one tag");

/** Body of POST /crm/masters. */
export const CreateCrmMasterRequestValidation = z.object({
  short_name: crmText,
  long_name: z.string().trim().min(1, "Enter a name"),
});

export type CreateCrmMasterRequest = z.infer<typeof CreateCrmMasterRequestValidation>;

export const CrmContactKindValidation = z.enum(["phone", "email"]);

/** Shown when a phone can't be converted to E.164; the portal never saves such a phone. */
export const CRM_INVALID_PHONE_MESSAGE = "Enter a valid phone number, like (515) 555-0100";

/** Phones must parse as a valid number (US default, same parser as the server); emails must be valid. */
export function crmContactValueIssue(kind: z.infer<typeof CrmContactKindValidation>, value: string): string | null {
  if (kind === "phone") {
    return parsePhoneNumberToE164Strict(value) ? null : CRM_INVALID_PHONE_MESSAGE;
  }
  return z.string().trim().email().safeParse(value).success ? null : "Enter a valid email address";
}

function refineCrmContactValue(
  contact: { kind: z.infer<typeof CrmContactKindValidation>; value: string },
  context: z.RefinementCtx,
): void {
  // Phones are always sent to the API as E.164 (+15155550100); the form converts its national format,
  // so a phone that isn't E.164 here couldn't be converted and must not be saved.
  const message = contact.kind === "phone"
    ? (parsePhoneNumberToE164Strict(contact.value) === contact.value ? null : CRM_INVALID_PHONE_MESSAGE)
    : crmContactValueIssue(contact.kind, contact.value);
  if (message) context.addIssue({ code: z.ZodIssueCode.custom, message, path: ["value"] });
}

const CrmContactBaseValidation = z.object({
  kind: CrmContactKindValidation,
  value: crmText,
  label: crmText.nullable(),
  tag_ids: requiredCrmTagIds,
});

export const CrmContactCreateValidation = CrmContactBaseValidation.superRefine(refineCrmContactValue);
/** Full replacement of an existing contact. */
export const CrmContactUpdateValidation = CrmContactBaseValidation
  .extend({ id: z.string().uuid() })
  .superRefine(refineCrmContactValue);

export const CrmAddressCreateValidation = z.object({
  label: crmText.nullable(),
  address_line_one: crmText.nullable(),
  address_line_two: crmText.nullable(),
  city: crmText.nullable(),
  state: crmText.nullable(),
  zipcode: crmText.nullable(),
  country: crmText.nullable(),
  county: crmText.nullable(),
  tag_ids: requiredCrmTagIds,
});

/** Full replacement of an existing address. */
export const CrmAddressUpdateValidation = CrmAddressCreateValidation.extend({ id: z.string().uuid() });

/** Type-specific record fields. Which ones a record type has comes from GET /crm/rules. */
export const CrmRecordTypeFieldsRequestValidation = z.object({
  notes: crmText.nullable(),
  shipping_instructions: crmText.nullable(),
  regular_hours: crmText.nullable(),
  current_week_hours: crmText.nullable(),
  next_week_hours: crmText.nullable(),
  marketing_fee: strictDecimalString.nullable(),
  active_account_codes: z.array(z.string()),
  harvest_iq_profile: crmText.nullable(),
  skip_harvest_iq_sync: z.boolean(),
  force_show_activities: z.boolean(),
  preferred_defer_payment_type: z.enum(["ach", "check"]).nullable(),
  deferred_payment_lien_status: z.literal("active").nullable(),
  signed_up_date: portalDate.nullable(),
  partner_names: z.array(z.string()),
  shirt_sizes: z.array(z.string()),
  original_acres: z.number().int().min(0).nullable(),
  bol_sequence: crmText.nullable(),
  is_commercial: z.boolean(),
  trader_id: z.string().uuid().nullable(),
}).partial();

export type CrmRecordTypeFieldsRequest = z.infer<typeof CrmRecordTypeFieldsRequestValidation>;

/** The record part of apply-changes: base fields are always sent in full. client_id is set once, at create. */
const CrmRecordRequestValidation = z.object({
  // Allowed types come from GET /crm/rules; the server rejects anything else.
  record_type: z.custom<CrmRecordType>((value: unknown): boolean => typeof value === "string" && value !== ""),
  owner_id: z.string().uuid().nullable(),
  short_name: crmText,
  long_name: crmText,
  tag_ids: crmTagIds,
}).merge(CrmRecordTypeFieldsRequestValidation);

/**
 * The record part of create: apply-changes' fields plus client_id (required by types that GET /crm/rules marks
 * requires_client_id) and onboarding_instance_id (only types it marks creates_customer; acked with the new customer).
 */
const CrmRecordCreateRequestValidation = CrmRecordRequestValidation.extend({
  client_id: crmText.nullable(),
  onboarding_instance_id: z.string().uuid().nullable(),
});

/**
 * Body of POST /crm/records/create-with-details. The portal creates into an existing group, or a new one
 * created with the record where the form allows it.
 */
export const CreateCrmRecordWithDetailsRequestValidation = z.object({
  master: z.union([
    z.object({ id: z.string().uuid() }).strict(),
    z.object({ short_name: crmText, long_name: crmText }).strict(),
  ]),
  record: CrmRecordCreateRequestValidation,
  contacts: z.array(CrmContactCreateValidation),
  addresses: z.array(CrmAddressCreateValidation),
});

export type CreateCrmRecordWithDetailsRequest = z.infer<typeof CreateCrmRecordWithDetailsRequestValidation>;

/** Body of PUT /crm/records/apply-changes. */
export const ApplyCrmRecordChangesRequestValidation = z.object({
  record_id: z.string().uuid(),
  expected_revision: z.number().int().min(1),
  record: CrmRecordRequestValidation,
  contacts: z.object({
    creates: z.array(CrmContactCreateValidation),
    updates: z.array(CrmContactUpdateValidation),
    delete_ids: z.array(z.string().uuid()),
  }),
  addresses: z.object({
    creates: z.array(CrmAddressCreateValidation),
    updates: z.array(CrmAddressUpdateValidation),
    delete_ids: z.array(z.string().uuid()),
  }),
});

export type ApplyCrmRecordChangesRequest = z.infer<typeof ApplyCrmRecordChangesRequestValidation>;
